import RevealOnScroll from '../../../components/base/RevealOnScroll';

const TEAM_IMAGE = 'https://public.readdy.ai/ai/img_res/06c1ae13dddc46cdc82216e684f81b89.jpg';

const differentiators = [
  {
    icon: 'ri-history-line',
    title: '35+ Years of Chicago Expertise',
    desc: "We've seen every roofing problem Chicago weather can create. Our team has been solving them since 1989 — shingle failures, flat roof ponding, freeze-thaw masonry damage, and everything in between.",
  },
  {
    icon: 'ri-home-heart-line',
    title: 'Family-Owned Accountability',
    desc: 'When you work with Green World, you deal directly with the Christo family. No middlemen, no rotating project managers. The same team that gives you the estimate is on your roof doing the work.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Written Estimates — Always',
    desc: "Every inspection results in a detailed written estimate. You know exactly what's being done, what materials are used, and what it costs before a single nail is driven.",
  },
  {
    icon: 'ri-tools-fill',
    title: 'All Systems, All Trades',
    desc: 'Flat roofs, shingles, TPO, EPDM, metal, tuckpointing, masonry, siding, gutters, sealcoating — we do it all in-house. One contractor. Complete exterior solutions.',
  },
  {
    icon: 'ri-snowflake-line',
    title: 'Built for Midwest Conditions',
    desc: "Chicago's freeze-thaw cycles, heavy snow loads, and brutal summers demand specific materials and techniques. We select products rated for our climate and install them the right way.",
  },
  {
    icon: 'ri-communication-line',
    title: 'Full Transparency, Start to Finish',
    desc: "We take pride in keeping customers informed throughout the entire project. You'll always know the project status, what's next, and when the job will be complete.",
  },
];

export default function WhyChooseUs() {
  return (
    <section className="bg-brand-cream py-24 relative overflow-hidden">
      {/* Decorative element */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-green/5 rounded-full translate-x-1/3 -translate-y-1/3" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: image with overlapping elements */}
          <RevealOnScroll direction="left" className="relative">
            {/* Main image */}
            <div className="relative w-full h-[520px] rounded-2xl overflow-hidden">
              <img
                src={TEAM_IMAGE}
                alt="Green World Construction roofing professionals"
                className="w-full h-full object-cover object-top"
              />
              {/* Subtle gradient at bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent" />
            </div>

            {/* Overlapping stat badges */}
            <div className="absolute -bottom-5 -right-4 bg-brand-gold p-6 rounded-2xl animate-float">
              <div className="font-display text-4xl font-bold text-white leading-none">35+</div>
              <div className="text-white/80 text-xs font-semibold mt-1.5">Years of<br />Excellence</div>
            </div>
            <div className="absolute top-6 -left-4 bg-brand-dark/90 backdrop-blur-sm border border-white/10 p-5 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-brand-gold/20 rounded-xl">
                  <i className="ri-home-heart-fill text-brand-gold text-lg" />
                </div>
                <div>
                  <div className="text-white font-bold text-sm">Family-Owned</div>
                  <div className="text-gray-400 text-xs">Since 1989</div>
                </div>
              </div>
            </div>

            {/* Project count pill */}
            <div className="absolute bottom-20 left-4 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-brand-green/10 rounded-lg">
                <i className="ri-home-gear-fill text-brand-green text-base" />
              </div>
              <div>
                <div className="font-bold text-brand-dark text-sm">5,000+ Projects</div>
                <div className="text-gray-500 text-xs">Completed in Chicagoland</div>
              </div>
            </div>
          </RevealOnScroll>

          {/* Right: content */}
          <RevealOnScroll direction="right" delay={100} className="flex flex-col">
            <span className="text-brand-green text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2 mb-4">
              <span className="w-8 h-px bg-brand-green inline-block" />
              Why Choose Us
            </span>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-brand-dark mb-5 leading-tight">
              Built on Craftsmanship.<br />
              <span className="text-brand-green italic">Delivered with Integrity.</span>
            </h2>
            <p className="text-gray-600 leading-relaxed mb-10">
              This is not basic roofing — this is a three-decade track record of doing the job right the
              first time, using premium materials, and standing behind every project we complete in
              Chicago and Chicagoland.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {differentiators.map((item) => (
                <div key={item.title} className="group flex gap-3.5">
                  <div className="w-10 h-10 flex items-center justify-center bg-brand-green/10 rounded-xl flex-shrink-0 mt-0.5 group-hover:bg-brand-green group-hover:text-white transition-all duration-300">
                    <i className={`${item.icon} text-brand-green group-hover:text-white text-base transition-colors`} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-brand-dark mb-1.5">{item.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </div>
      </div>
    </section>
  );
}
